import { z } from 'zod'
import { zCommonResponse, zPaginationBuilder } from './common.schemas'
import {
	zCouponStatus,
	zIssueReason,
	zMemberCouponWithMember,
	zCouponMaster,
	zCouponValidityType,
	zCouponUseLimitType,
	zMemberCouponUsageHistory
} from './coupon.schemas'

// List coupons
const zListCouponsRequest = z.object({
	memberCode: z.string().optional(),
	couponMasterId: z.coerce.number().optional(),
	status: z.union([zCouponStatus, z.array(zCouponStatus)]).optional(),
	expiresAtMin: z.string().optional(),
	expiresAtMax: z.string().optional(),
	page: z
		.any()
		.transform((val) => (val ? Number(val) : 1))
		.optional(),
	limit: z
		.any()
		.transform((val) => (val ? Number(val) : 20))
		.optional(),
	sortKey: z.enum(['couponId', 'issuedAt', 'expiresAt', 'status']).optional(),
	sort: z.enum(['asc', 'desc']).optional()
})

const zListCouponsResponse = zCommonResponse.extend({
	data: z.object({
		coupons: z.array(zMemberCouponWithMember),
		total: z.number(),
		page: z.number(),
		limit: z.number()
	})
})

// Coupon Usage History
const zListCouponUsageHistoryRequest = z.object({
	memberCouponId: z.coerce.number().optional(),
	memberId: z.coerce.number().optional(),
	page: z
		.any()
		.transform((val) => (val ? Number(val) : 1))
		.optional(),
	limit: z
		.any()
		.transform((val) => (val ? Number(val) : 20))
		.optional()
})

const zListCouponUsageHistoryResponse = zCommonResponse.extend({
	data: z.object({
		usageHistory: z.array(zMemberCouponUsageHistory),
		total: z.number(),
		page: z.number(),
		limit: z.number()
	})
})

// Create coupon
const zCreateCouponRequest = z.object({
	memberCode: z.string(),
	couponMasterId: z.number().int().positive(),
	issueReason: zIssueReason
})

const zCreateCouponResponse = zCommonResponse.extend({
	data: z.object({
		couponId: z.number()
	})
})

// Update coupon status
const zUpdateCouponStatusPathParams = z.object({
	couponId: z.coerce.number()
})

const zUpdateCouponStatusRequest = z.object({
	status: zCouponStatus,
	usedAt: z.string().optional()
})

const zUpdateCouponStatusResponse = zCommonResponse

// Get coupon details
const zGetCouponPathParams = z.object({
	couponId: z.coerce.number()
})

const zGetCouponResponse = zCommonResponse.extend({
	data: zMemberCouponWithMember
})

// Create coupon usage history
const zCreateCouponUsageHistoryRequest = z.object({
	memberCouponId: z.number().int().positive(),
	usedAt: z.string().optional()
})

const zCreateCouponUsageHistoryResponse = zCommonResponse.extend({
	data: z
		.object({
			usageId: z.number(),
			updatedUsedCount: z.number()
		})
		.optional()
})

// Delete coupon usage history
const zDeleteCouponUsageHistoryResponse = zCommonResponse.extend({
	data: z
		.object({
			updatedUsedCount: z.number()
		})
		.optional()
})

// Batch create coupons
const zBatchCreateCouponRequest = z.object({
	couponMasterId: z.coerce.number().int().positive(),
	quantity: z.number().int().min(1).max(10),
	targetMode: z.enum(['all_active', 'member_codes']).optional(),
	memberCodes: z.array(z.string()).optional(),
	expiresAt: z.string().optional()
})

const zBatchCreateCouponResponse = zCommonResponse.extend({
	data: z.object({
		membersProcessed: z.number(),
		couponsCreated: z.number(),
		failedMemberCodes: z.array(z.string())
	})
})

// Coupon Master CRUD
const zCreateCouponMasterRequest = z.object({
	couponType: z.string().max(100).optional(),

	// UI display fields
	title: z.string().min(1).max(255),
	image: z.string().max(100).optional(),
	emojiDisplay: z.string().max(10).optional(),
	description: z.string().optional(),
	userGuide: z.string().optional(),
	sortOrder: z.coerce.number().int().nonnegative().optional(),

	// Validity and usage rules
	validityType: zCouponValidityType.optional(),
	defaultExpiryMonths: z.coerce.number().int().positive().optional(),
	periodStartAt: z.string().optional(),
	periodEndAt: z.string().optional(),
	useLimitType: zCouponUseLimitType.optional(),
	maxUsesPerMember: z.coerce.number().int().nonnegative().optional(),
	exchangePoints: z.coerce.number().int().nonnegative().optional(),
	isActive: z.boolean().optional()
})

const zUpdateCouponMasterRequest = zCreateCouponMasterRequest.partial()

const zCouponMasterPathParams = z.object({
	couponMasterId: z.coerce.number()
})

const zListCouponMastersRequest = z.object({
	includeInactive: z
		.string()
		.optional()
		.transform((val) => val === 'true' || val === '1')
})

const zListCouponMastersResponse = zCommonResponse.extend({
	data: z.array(zCouponMaster)
})

const zCouponMasterResponse = zCommonResponse.extend({
	data: zCouponMaster
})

export {
	zListCouponsRequest,
	zListCouponsResponse,
	zCreateCouponRequest,
	zCreateCouponResponse,
	zUpdateCouponStatusPathParams,
	zUpdateCouponStatusRequest,
	zUpdateCouponStatusResponse,
	zGetCouponPathParams,
	zGetCouponResponse,
	zBatchCreateCouponRequest,
	zBatchCreateCouponResponse,
	zCreateCouponMasterRequest,
	zUpdateCouponMasterRequest,
	zCouponMasterPathParams,
	zListCouponMastersRequest,
	zListCouponMastersResponse,
	zCouponMasterResponse,
	zListCouponUsageHistoryRequest,
	zListCouponUsageHistoryResponse,
	zCreateCouponUsageHistoryRequest,
	zCreateCouponUsageHistoryResponse,
	zDeleteCouponUsageHistoryResponse
}
